import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// @ts-ignore
import confetti from 'canvas-confetti';
import html2canvas from 'html2canvas';
import Swal from 'sweetalert2';
import { haversineDistance, Coordinates } from '../utils/haversine';
import { Play, Pause, CheckCircle, RefreshCcw } from 'lucide-react';

const MIN_MOVEMENT_METERS = 10;
const KM_TO_MILES = 0.621371;

const LocationTracker: React.FC = () => {
  const { brandId, userId } = useParams();
  const navigate = useNavigate();

  const [totalDistance, setTotalDistance] = useState<number>(0);
  const [goalDistance, setGoalDistance] = useState<number | null>(null);
  const [pendingGoal, setPendingGoal] = useState<string>('');
  const [tracking, setTracking] = useState<boolean>(false);
  const [duration, setDuration] = useState<number>(0);
  const [units, setUnits] = useState<'km' | 'miles'>('km');

  const [showFinishPopup, setShowFinishPopup] = useState<boolean>(false);
  const [goalReached, setGoalReached] = useState<boolean>(false);
  const [halfwayReached, setHalfwayReached] = useState<boolean>(false);

  const watchIdRef = useRef<number | null>(null);
  const prevCoordsRef = useRef<Coordinates | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // ─── On-mount: restore saved values (but do NOT auto-resume tracking) ───
  useEffect(() => {
    const savedDistance = localStorage.getItem('LT_totalDistance');
    if (savedDistance !== null) {
      setTotalDistance(parseFloat(savedDistance));
    }

    const savedDuration = localStorage.getItem('LT_duration');
    if (savedDuration !== null) {
      setDuration(parseInt(savedDuration, 10));
    }

    const savedGoal = localStorage.getItem('LT_goalDistance');
    if (savedGoal !== null) {
      setGoalDistance(parseFloat(savedGoal));
    }

    const savedUnits = localStorage.getItem('LT_units');
    if (savedUnits === 'km' || savedUnits === 'miles') {
      setUnits(savedUnits);
    }

    const savedPending = localStorage.getItem('LT_pendingGoal');
    if (savedPending !== null) {
      setPendingGoal(savedPending);
    }

    const savedCoordsJson = localStorage.getItem('LT_prevCoords');
    if (savedCoordsJson) {
      try {
        prevCoordsRef.current = JSON.parse(savedCoordsJson) as Coordinates;
      } catch {
        prevCoordsRef.current = null;
      }
    }

    const savedGoalReached = localStorage.getItem('LT_goalReached');
    if (savedGoalReached === 'true') {
      setGoalReached(true);
    }

    const savedHalfwayReached = localStorage.getItem('LT_halfwayReached');
    if (savedHalfwayReached === 'true') {
      setHalfwayReached(true);
    }
  }, []);

  // ─── Persist totalDistance whenever it changes ───
  useEffect(() => {
    localStorage.setItem('LT_totalDistance', totalDistance.toString());
  }, [totalDistance]);

  // ─── Persist duration whenever it changes ───
  useEffect(() => {
    localStorage.setItem('LT_duration', duration.toString());
  }, [duration]);

  // ─── Persist goalDistance whenever it changes ───
  useEffect(() => {
    if (goalDistance !== null) {
      localStorage.setItem('LT_goalDistance', goalDistance.toString());
    } else {
      localStorage.removeItem('LT_goalDistance');
    }
  }, [goalDistance]);

  // ─── Persist units whenever it changes ───
  useEffect(() => {
    localStorage.setItem('LT_units', units);
  }, [units]);

  // ─── Persist pendingGoal whenever it changes ───
  useEffect(() => {
    localStorage.setItem('LT_pendingGoal', pendingGoal);
  }, [pendingGoal]);

  // ─── Persist goalReached & halfwayReached ───
  useEffect(() => {
    localStorage.setItem('LT_goalReached', goalReached ? 'true' : 'false');
  }, [goalReached]);

  useEffect(() => {
    localStorage.setItem('LT_halfwayReached', halfwayReached ? 'true' : 'false');
  }, [halfwayReached]);

  // ─── Timer effect: increment duration when tracking ───
  useEffect(() => {
    if (tracking) {
      timerRef.current = setInterval(() => {
        setDuration((d) => {
          const next = d + 1;
          localStorage.setItem('LT_duration', next.toString());
          return next;
        });
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [tracking]);

  // ─── Goal and halfway notifications ───
  useEffect(() => {
    const km = totalDistance / 1000;
    if (!goalDistance) return;
    const percent = km / goalDistance;

    if (!goalReached && percent >= 1) {
      setGoalReached(true);
      confetti({ particleCount: 180, spread: 100, origin: { y: 0.6 } });
      try {
        const message = new SpeechSynthesisUtterance(
          'Congratulations! You have reached your goal.'
        );
        speechSynthesis.speak(message);
      } catch (error) {
        console.warn('Speech failed:', error);
      }
    }

    if (!halfwayReached && percent >= 0.5) {
      setHalfwayReached(true);
      try {
        const message = new SpeechSynthesisUtterance(
          "You're halfway to your goal. Keep going!"
        );
        speechSynthesis.speak(message);
      } catch (error) {
        console.warn('Speech failed:', error);
      }
    }
  }, [totalDistance, goalDistance, goalReached, halfwayReached]);

  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getDistanceNumber = () => {
    const km = totalDistance / 1000;
    return units === 'km' ? km.toFixed(2) : (km * KM_TO_MILES).toFixed(2);
  };

  const getDistanceUnit = () => (units === 'km' ? ' km' : ' miles');

  const getDistanceDisplay = () => {
    const km = totalDistance / 1000;
    return units === 'km'
      ? `${km.toFixed(2)} km`
      : `${(km * KM_TO_MILES).toFixed(2)} miles`;
  };

  const getGoalDisplay = () => {
    if (!goalDistance) return '--';
    return units === 'km'
      ? `${goalDistance.toFixed(1)} km`
      : `${(goalDistance * KM_TO_MILES).toFixed(1)} miles`;
  };

  const getProgress = () => {
    if (!goalDistance) return 0;
    const km = totalDistance / 1000;
    return Math.min((km / goalDistance) * 100, 100);
  };

  // ─── startTracking: show popup and attach geolocation watcher ───
  const startTracking = () => {
    if (!tracking && 'geolocation' in navigator) {
      // Show the SweetAlert2 popup
      Swal.fire({
        title: 'Please keep this screen open',
        text: 'Do not close your device.',
        icon: 'info',
        showCloseButton: true,
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true,
        allowOutsideClick: false,
        allowEscapeKey: false,
        position: 'center',
      });

      // Clear any previously saved coordinates so we don't get a big jump
      prevCoordsRef.current = null;
      localStorage.removeItem('LT_prevCoords');

      const id = navigator.geolocation.watchPosition(
        (pos) => {
          const newCoords: Coordinates = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          };
          const prev = prevCoordsRef.current;
          if (prev) {
            const dist = haversineDistance(prev, newCoords);
            if (dist >= MIN_MOVEMENT_METERS) {
              // Use functional update to avoid stale closures
              setTotalDistance((d) => {
                const updatedDistance = d + dist;
                localStorage.setItem(
                  'LT_totalDistance',
                  updatedDistance.toString()
                );
                return updatedDistance;
              });

              // Update prevCoordsRef and persist it
              prevCoordsRef.current = newCoords;
              localStorage.setItem(
                'LT_prevCoords',
                JSON.stringify(newCoords)
              );
            }
          } else {
            // First GPS fix—just set prevCoordsRef
            prevCoordsRef.current = newCoords;
            localStorage.setItem('LT_prevCoords', JSON.stringify(newCoords));
          }
        },
        (err) => console.error('Geolocation error:', err),
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 1000 }
      );

      watchIdRef.current = id;
      setTracking(true);
      localStorage.setItem('LT_tracking', 'true');
    }
  };

  // ─── stopTracking: clear geolocation watcher ───
  const stopTracking = () => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setTracking(false);
    localStorage.setItem('LT_tracking', 'false');
  };

  // ─── resetData: clear everything and localStorage ───
  const resetData = () => {
    stopTracking();
    setTotalDistance(0);
    setDuration(0);
    prevCoordsRef.current = null;
    setShowFinishPopup(false);
    setGoalReached(false);
    setHalfwayReached(false);
    setGoalDistance(null);
    setPendingGoal('');

    localStorage.removeItem('LT_totalDistance');
    localStorage.removeItem('LT_duration');
    localStorage.removeItem('LT_prevCoords');
    localStorage.removeItem('LT_tracking');
    localStorage.removeItem('LT_goalDistance');
    localStorage.removeItem('LT_units');
    localStorage.removeItem('LT_pendingGoal');
    localStorage.removeItem('LT_goalReached');
    localStorage.removeItem('LT_halfwayReached');
  };

  // ─── finishActivity: stop tracking and show summary popup ───
  const finishActivity = () => {
    stopTracking();
    setShowFinishPopup(true);
  };

  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  const handleScreenshot = () => {
    const popup = document.querySelector('.popup-content');
    if (!popup) return;
    html2canvas(popup as HTMLElement).then((canvas) => {
      const image = canvas.toDataURL('image/png');
      window.open(image, '_blank');
    });
  };

  return (
    <div className="tracker-page" style={{ paddingTop: '0.5rem' }}>
      <div className="goal-tracker-container">
        <div className="tracker-main-content">
          <div className="unit-toggle">
            <label
              style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}
            >
              <input
                type="radio"
                value="km"
                checked={units === 'km'}
                onChange={() => setUnits('km')}
              />
              km
            </label>
            <label
              style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}
            >
              <input
                type="radio"
                value="miles"
                checked={units === 'miles'}
                onChange={() => setUnits('miles')}
              />
              miles
            </label>
          </div>

          <div className="tracker-display">
            <p className="label">Duration</p>
            <p className="value">{formatTime(duration)}</p>

            <p className="label">Distance</p>
            <p className="value-distance">
              <span className="distance-number">{getDistanceNumber()}</span>
              <span className="distance-unit">{getDistanceUnit()}</span>
            </p>

            <div className="button-group">
              {!tracking ? (
                <button onClick={startTracking} className="btn btn-outline start">
                  <Play size={28} />
                </button>
              ) : (
                <button onClick={stopTracking} className="btn btn-outline stop">
                  <Pause size={28} />
                </button>
              )}
              <button onClick={finishActivity} className="btn btn-outline finish">
                <CheckCircle size={28} />
              </button>
              <button onClick={resetData} className="btn btn-outline reset">
                <RefreshCcw size={28} />
              </button>
            </div>

            {goalDistance && (
              <>
                <p className="label">Goal</p>
                <p className="value">{getGoalDisplay()}</p>
              </>
            )}

            {!goalDistance && (
              <div
                className="goal-input"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                  marginTop: '1.5rem',
                  alignItems: 'center',
                }}
              >
                <label htmlFor="goal" style={{ fontWeight: 'bold' }}>
                  Set Activity Goal (KM)
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="number"
                    id="goal"
                    step="0.1"
                    value={pendingGoal}
                    onChange={(e) => setPendingGoal(e.target.value)}
                  />
                  <button
                    className="btn set-goal"
                    onClick={() => {
                      const val = parseFloat(pendingGoal);
                      if (!isNaN(val) && val > 0) {
                        setGoalDistance(val);
                      }
                    }}
                  >
                    Set
                  </button>
                </div>
              </div>
            )}

            <p className="label bebas">Progress</p>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${getProgress()}%` }}
              />
            </div>
            <p className="progress-percent">{getProgress().toFixed(0)}%</p>

            {goalDistance && (
              <div className="motivation-message">
                <p>🔥 You're doing great! Keep moving toward your goal!</p>
              </div>
            )}
          </div>
        </div>

        {showFinishPopup && (
          <div className="popup">
            <div className="popup-content">
              <h3>🎉 Well done!</h3>
              <div className="popup-details">
                <div className="popup-row bebas">
                  <span className="popup-label">Distance:</span>
                  <span className="popup-value">{getDistanceDisplay()}</span>
                </div>
                <div className="popup-row bebas">
                  <span className="popup-label">Time:</span>
                  <span className="popup-value">{formatTime(duration)}</span>
                </div>
              </div>
              <p className="popup-instruction">
                📸 Screenshot and submit this in any of your challenges.
              </p>
              <div className="popup-actions">
                {!isIOS && (
                  <button onClick={handleScreenshot} className="btn">
                    📸 Screenshot
                  </button>
                )}
                <button
                  onClick={() => setShowFinishPopup(false)}
                  className="btn"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationTracker;

